import isEqual from 'lodash/isEqual';
import {
    ADD_MENU_ENTRY_TO_CART,
    CHANGE_ORDER_LINE_MODIFIERS,
    DEC_ORDER_LINE_QUANTITY,
    INC_ORDER_LINE_QUANTITY,
} from 'new-constants/order-line.constants';
import { DISCOUNT_FAILURE, SEND_ORDER_FAILURE, SEND_ORDER_SUCCESS } from 'new-constants/api.constants';
import { STRIPE_SUCCESS, VIPPS_SUCCESS } from 'new-constants/payment.constants';
import {
    DONE_INCOMPATIBLE_CART_CONFIRMATION,
    DONE_LOGOUT_CONFIRMATION,
    OPEN_REGISTRATION_POPUP,
} from 'new-constants/popup.constants';
import { OrderLineState } from './order-line.reducer.types';
import { OrderLine, OrderLineToken } from '../helpers/order-line.helper.types';
import {
    CHECK_ADYEN_PAYMENT_SUCCESS,
    CHECK_PAYMENT_STATUS_SUCCESS,
    CUSTOMER_SUCCESS,
    ORDER_SUCCESS,
    REPEAT_ORDER_SUCCESS, SUBMIT_APPLE_PAY_SUCCESS, SUBMIT_GOOGLE_PAY_SUCCESS
} from '../constants/api.constants';
import { ServerOrderLine } from '../helpers/purchase.helper.types';
import { PaymentStatuses } from "../constants/common.constants";
import { ADYEN_SUCCESS } from '../constants/payment.constants';
import { CAUTH_CLEAR_CUSTOMER_DATA, START } from '../constants/app-state.constants';

export const defaultOrderLineCacheEntry = [];

const initialState: OrderLineState = {
    temporaryCache: {},
    cache: {},
    defaultTakeAwayItemsAdded: {},
};

function isOrderLinesEqual(orderLine1: OrderLine, orderLine2: OrderLine): boolean {
    return (
        orderLine1.menuEntryId === orderLine2.menuEntryId &&
        isEqual(orderLine1.modifierSelections, orderLine2.modifierSelections)
    );
}

function getRandomInt(max) {
    return Math.floor(Math.random() * Math.floor(max));
}

function getOrderLineFromServerOrderLine(serverOrderLine: ServerOrderLine): OrderLine {
    return {
        token: Date.now() + getRandomInt(1000),
        quantity: serverOrderLine.quantity,
        menuEntryId: serverOrderLine.menuEntryId,
        modifierSelections: serverOrderLine.modifierSelections || {},
    };
}

export const orderLineReducer = (previousState = initialState, action): OrderLineState => {
    switch (action && action.type) {
        case ADD_MENU_ENTRY_TO_CART:
            const previousOrderLines = previousState.cache[action.restaurantId] || defaultOrderLineCacheEntry;

            const newOrderLine = {
                token: Date.now(),
                menuEntryId: action.menuEntryId,
                quantity: action.quantity,
                modifierSelections: action.modifierSelections,
            };

            if (action.isTemporarySave) {
                return {
                    ...previousState,
                    temporaryCache: {
                        ...previousState.temporaryCache,
                        [action.restaurantId]: newOrderLine,
                    },
                };
            }

            if (!action.isAddToCart) {
                return previousState;
            }

            const existingOrderLine = previousOrderLines.find(orderLine => isOrderLinesEqual(orderLine, newOrderLine));

            let newOrderLines = null;

            if (existingOrderLine) {
                newOrderLines = [
                    ...previousOrderLines.filter(orderLine => orderLine.token !== existingOrderLine.token),
                    {
                        ...existingOrderLine,
                        quantity: existingOrderLine.quantity + newOrderLine.quantity,
                    },
                ];
            } else {
                newOrderLines = [...previousOrderLines, newOrderLine];
            }
            const previousAddedDefaultTakeAwayItems = previousState.defaultTakeAwayItemsAdded[action.restaurantId] || [];
            return {
                ...previousState,
                cache: {
                    ...previousState.cache,
                    [action.restaurantId]: newOrderLines,
                },
                defaultTakeAwayItemsAdded: action.isDefaultTakeAwayItem ? {
                    ...previousState.defaultTakeAwayItemsAdded,
                    [action.restaurantId]: [...previousAddedDefaultTakeAwayItems, newOrderLine.menuEntryId],
                } : {
                    [action.restaurantId]: previousAddedDefaultTakeAwayItems,
                },
            };

            break;

        case INC_ORDER_LINE_QUANTITY: {
            const previousOrderLines = previousState.cache[action.restaurantId];

            const orderLineToChange = previousOrderLines.find(orderLine => orderLine.token === action.token);

            return {
                ...previousState,
                cache: {
                    ...previousState.cache,
                    [action.restaurantId]: [
                        ...previousOrderLines.filter(orderLine => orderLine.token !== action.token),
                        {
                            ...orderLineToChange,
                            quantity: orderLineToChange.quantity + 1,
                        },
                    ],
                },
            };
        }
            break;

        case DEC_ORDER_LINE_QUANTITY: {
            const previousOrderLines = previousState.cache[action.restaurantId];

            const orderLineToChange = previousOrderLines.find(orderLine => orderLine.token === action.token);

            const otherOrderLines = previousOrderLines.filter(orderLine => orderLine.token !== action.token); //TODO rewrite using .map

            return {
                ...previousState,
                cache: {
                    ...previousState.cache,
                    [action.restaurantId]:
                        orderLineToChange.quantity > 1
                            ? [
                                ...otherOrderLines,
                                {
                                    ...orderLineToChange,
                                    quantity: orderLineToChange.quantity - 1,
                                },
                            ]
                            : otherOrderLines,
                },
            };
        }
            break;

        case CHANGE_ORDER_LINE_MODIFIERS:
            return {
                ...previousState,
                cache: {
                    ...previousState.cache,
                    [action.restaurantId]: previousState.cache[action.restaurantId].map(orderLine => {
                        if (orderLine.token === action.token) {
                            return {
                                ...orderLine,
                                modifierSelections: {
                                    ...orderLine.modifierSelections,
                                    [action.modifierGroupId]: action.modifierSelections,
                                },
                            };
                        } else {
                            return orderLine;
                        }
                    }),
                },
            };
            break;

        case SEND_ORDER_SUCCESS:
            if (action.payload.isOrderSent) {
                return {
                    ...previousState,
                    cache: {
                        ...previousState.cache,
                        [action.payload.data.order.pickupPlaceId ||
                        action.payload.data.order.restaurantId]: defaultOrderLineCacheEntry,
                    },
                    defaultTakeAwayItemsAdded: {
                        ...previousState.defaultTakeAwayItemsAdded,
                        [action.payload.data.order.pickupPlaceId ||
                        action.payload.data.order.restaurantId]: [],
                    }
                };
            }

            return previousState;
            break;

        case STRIPE_SUCCESS:
        case VIPPS_SUCCESS:
            return {
                ...previousState,
                cache: {
                    ...previousState.cache,
                    [action.restaurantId]: defaultOrderLineCacheEntry,
                },
                defaultTakeAwayItemsAdded: {
                    ...previousState.defaultTakeAwayItemsAdded,
                    [action.restaurantId]: [],
                },
            };
            break;

        case ADYEN_SUCCESS:
            if (action.payload.action.type === 'redirect' || action.payload.action.type === 'threeDS2' || action.payload.action.type === 'swish') {
                return {
                    ...previousState,
                }
            }
            return {
                ...previousState,
                cache: {
                    ...previousState.cache,
                    [action.payload.restaurantId]: defaultOrderLineCacheEntry,
                },
                defaultTakeAwayItemsAdded: {
                    ...previousState.defaultTakeAwayItemsAdded,
                    [action.payload.restaurantId]: [],
                },
            };
            break;

        case SUBMIT_GOOGLE_PAY_SUCCESS:
        case SUBMIT_APPLE_PAY_SUCCESS:
            if (action.payload.action.type === 'redirect' || action.payload.action.type === 'threeDS2') {
                return {
                    ...previousState,
                }
            }
            return {
                ...previousState,
                cache: {
                    ...previousState.cache,
                    [action.payload.restaurantId]: defaultOrderLineCacheEntry,
                },
                defaultTakeAwayItemsAdded: {
                    ...previousState.defaultTakeAwayItemsAdded,
                    [action.payload.restaurantId]: [],
                },
            };
            break;


        case CHECK_PAYMENT_STATUS_SUCCESS:
            if (!(action.payload.isAdyenOrder && action.payload.data.status === PaymentStatuses.complete)) {
                return previousState;
            }

            return {
                ...previousState,
                cache: {
                    ...previousState.cache,
                    [action.payload.currentRestaurantId]: defaultOrderLineCacheEntry,
                },
                defaultTakeAwayItemsAdded: {
                    ...previousState.defaultTakeAwayItemsAdded,
                    [action.payload.currentRestaurantId]: [],
                },
            };
            break;

        case CHECK_ADYEN_PAYMENT_SUCCESS:
            if (action.payload.data.resultCode !== PaymentStatuses.authorised) {
                return previousState;
            }

            return {
                ...previousState,
                cache: {
                    ...previousState.cache,
                    [action.payload.currentRestaurantId]: defaultOrderLineCacheEntry,
                },
                defaultTakeAwayItemsAdded: {
                    ...previousState.defaultTakeAwayItemsAdded,
                    [action.payload.currentRestaurantId]: [],
                },
            };
            break;


        case ORDER_SUCCESS:
            if (!(action.payload.isScheduledForCancel && action.payload.data.order.isPaid)) {
                return previousState;
            }

            return {
                ...previousState,
                cache: {
                    ...previousState.cache,
                    [action.payload.data.order.pickupPlaceId || action.payload.data.order.restaurantId]: defaultOrderLineCacheEntry
                },
                defaultTakeAwayItemsAdded: {
                    ...previousState.defaultTakeAwayItemsAdded,
                    [action.payload.data.order.pickupPlaceId || action.payload.data.order.restaurantId]: [],
                },
            };
            break;


        case OPEN_REGISTRATION_POPUP:
            if (!action.showPopup && previousState.temporaryCache[action.restaurantId]) {
                return {
                    ...previousState,
                    temporaryCache: {
                        ...previousState.temporaryCache,
                        [action.restaurantId]: null,
                    },
                    cache: {
                        ...previousState.cache,
                        [action.restaurantId]: [
                            ...(previousState.cache[action.restaurantId] || defaultOrderLineCacheEntry),
                            previousState.temporaryCache[action.restaurantId],
                        ],
                    },
                };
            }

            return previousState;
            break;

        case DONE_LOGOUT_CONFIRMATION:
            return {
                ...previousState,
                temporaryCache: {},
                cache: {},
                defaultTakeAwayItemsAdded: {},
            };
            break;

        case CAUTH_CLEAR_CUSTOMER_DATA:
            const previousCache = previousState.cache;
            const previousDefaultOrderLineCacheEntry = previousState.defaultTakeAwayItemsAdded;
            action.restaurantIds.forEach(id => delete previousCache[id]);
            action.restaurantIds.forEach(id => delete defaultOrderLineCacheEntry[id]);
            return {
                ...previousState,
                temporaryCache: {},
                cache: {
                    ...previousCache,
                },
                defaultTakeAwayItemsAdded: {
                    ...previousDefaultOrderLineCacheEntry,
                }
            };
            break;

        case DONE_INCOMPATIBLE_CART_CONFIRMATION:
            return {
                ...previousState,
                temporaryCache: {
                    ...previousState.temporaryCache,
                    [action.restaurantId]: null,
                },
                cache: {
                    ...previousState.cache,
                    [action.restaurantId]: [previousState.temporaryCache[action.restaurantId]],
                },
            };
            break;

        case SEND_ORDER_FAILURE:
        case DISCOUNT_FAILURE:
            if (action.meta?.data?.syncMenu?.removeEntryIdsFromCart && !action.meta.isSuppressError) {
                return {
                    ...previousState,
                    cache: {
                        ...previousState.cache,
                        [action.meta.restaurantId]: previousState.cache[action.meta.restaurantId].filter(
                            orderLine => !action.meta.data?.syncMenu?.removeEntryIdsFromCart.includes(orderLine.menuEntryId)
                        ),
                    },
                };
            }

            return previousState;

            break;

        case REPEAT_ORDER_SUCCESS:
            return {
                ...previousState,
                cache: {
                    ...previousState.cache,
                    [action.payload.data.restaurantId]: action.payload.data.orderLines.map(getOrderLineFromServerOrderLine)
                }
            };
            break;

        case CUSTOMER_SUCCESS:
            if (action.payload.previousRestaurant?.restaurantId !== action.payload.data?.restaurant?.restaurantId &&
                action.payload.previousRestaurant?.menuVersion === action.payload.data?.restaurant?.menuVersion) {
                return {
                    ...previousState,
                    cache: {
                        ...previousState.cache,
                        [action.payload.data.restaurant.restaurantId]: previousState.cache[action.payload.previousRestaurant?.restaurantId]
                    }
                };
            }
            return previousState;
            break;

        default:
            return previousState;
    }
};
